import IconsEnum from '@enums/icons.enum'
import { Button, Campaign, defaultImage, Media, Tag, UserInDetails } from '@hub/shared'
import { SqCarousel } from '@squidit/react-css'
import React, { useCallback, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import MetricBarComponent from '../metric-bar/metric-bar.component'
import './media-approved-card.component.scoped.scss'

interface Props extends React.HTMLAttributes<HTMLDivElement> {
  media: Media
  socialNetwork: string
  campaign: Campaign
  userInCampaign: UserInDetails
}

export default ({ media, socialNetwork, campaign, userInCampaign, className = '', style }: Props) => {
  const { t, i18n } = useTranslation('mediaApproval')
  const iconsEnum = useMemo(() => IconsEnum, [])
  const [videoError, setVideoError] = useState(false)

  const isContentApproved = useMemo(() => {
    return media?.referencias?.find((referencia) => referencia?.id === campaign?._id)?.gamification
  }, [media, campaign])

  const formatHour = useCallback((date: string | Date) => {
    const dateObj = new Date(date)
    return `${dateObj.getHours().toString().padStart(2, '0')}:${dateObj.getMinutes().toString().padStart(2, '0')}`
  }, [])

  const imageFallback = useMemo(() => {
    return (
      <img
        src={media?.imagens?.resolucaoPadrao?.url}
        alt={media?.legenda}
        title={media?.legenda}
        onError={({ currentTarget }) => {
          currentTarget.onerror = null
          currentTarget.src = defaultImage
        }}
      />
    )
  }, [media?.imagens?.resolucaoPadrao?.url, media?.legenda])

  const getElementMidiasCarousel = (media) => {
    return media?.carousel?.map((slide) => {
      return slide?.tipo === 'video' ? (
        <video controls>
          <source src={slide?.url} onError={() => setVideoError(true)} />
        </video>
      ) : (
        <img src={slide?.url} alt={media?.legenda} style={{ display: 'block' }} />
      )
    })
  }

  const postInstagram = useCallback(() => {
    return (
      <div className="wrapper-content-approval">
        <div className="infos-content-tag">
          <div className="infos-content">
            <Tag
              icon={<i className={`${iconsEnum.startDate}`}></i>}
              labels={[
                <span style={{ fontSize: '0.9rem' }}>{t('date')}</span>,
                <span className="text-bold" style={{ fontSize: '0.9rem' }}>
                  {new Date(media?.criadoEm).toLocaleDateString(i18n.language)}
                </span>,
              ]}
              separator
            />
            <Tag
              icon={<i className={`${iconsEnum.time}`}></i>}
              labels={[
                <span style={{ fontSize: '0.9rem' }}>{t('hour')}</span>,
                <span className="text-bold" style={{ fontSize: '0.9rem' }}>
                  {formatHour(media?.criadoEm)}
                </span>,
              ]}
              separator
            />
            {isContentApproved ? (
              <Tag
                icon={<i className={`${iconsEnum.gamification}`}></i>}
                labels={[
                  <span className="text-bold" style={{ fontSize: '0.9rem' }}>
                    {t('punctuated')}
                  </span>,
                ]}
                separator
              />
            ) : null}
          </div>
        </div>
        {['imagem', 'imagem_stories'].includes(media?.tipo) && <div className="post-content">{imageFallback}</div>}
        {!['imagem', 'imagem_stories'].includes(media?.tipo) && (
          <div className="post-content">
            {['video', 'video_stories'].includes(media?.tipo) ? (
              media?.videos && !videoError ? (
                <div>
                  <video controls>
                    <source src={media?.videos?.resolucaoPadrao?.url} onError={() => setVideoError(true)} />
                  </video>
                </div>
              ) : (
                imageFallback
              )
            ) : null}
            {['carousel'].includes(media?.tipo) && (
              <div className="post-content">
                {media?.tipo === 'carousel' && media?.carousel ? (
                  <SqCarousel showPagination slides={getElementMidiasCarousel(media)} />
                ) : (
                  imageFallback
                )}
              </div>
            )}
          </div>
        )}
        <div className="post-description">
          <p className="description">
            <span className="username text-bold">@{media?.usuario?.username}</span>
            {` ${media?.legenda}`}
          </p>
        </div>
        <MetricBarComponent media={media} />
      </div>
    )
  }, [media, formatHour, t, iconsEnum, i18n.language, imageFallback, videoError, isContentApproved])

  const postYoutube = useCallback(() => {
    return (
      <div className="wrapper-content-approval">
        <div className="infos-content-tag">
          <div className="infos-content">
            <Tag
              icon={<i className={`${iconsEnum.startDate}`}></i>}
              labels={[
                <span style={{ fontSize: '0.9rem' }}>{t('created')}</span>,
                <span className="text-bold" style={{ fontSize: '0.9rem' }}>
                  {new Date(media?.criadoEm).toLocaleDateString(i18n.language)}
                </span>,
              ]}
              separator
            />
            <Tag
              icon={<i className={`${iconsEnum.startDate}`}></i>}
              labels={[
                <span style={{ fontSize: '0.9rem' }}>{t('publicated')}</span>,
                <span className="text-bold" style={{ fontSize: '0.9rem' }}>
                  {new Date(media?.obtidoEm).toLocaleDateString(i18n.language)}
                </span>,
              ]}
              separator
            />
          </div>
        </div>
        <div className="post-content">
          <iframe className="video" src={`https://www.youtube.com/embed/${media?.uid}`} title={media?.url} allowFullScreen />
        </div>
        <div className="post-description">
          <h5 className="m-0">{media?.legenda}</h5>
        </div>
        <MetricBarComponent media={media} />
      </div>
    )
  }, [media, t, iconsEnum, i18n.language])

  const postTwitter = useCallback(() => {
    return (
      <div className="wrapper-content-approval">
        <div className="infos-content-tag">
          <div className="infos-content">
            <Tag
              icon={<i className={`${iconsEnum.startDate}`}></i>}
              labels={[
                <span style={{ fontSize: '0.9rem' }}>{t('date')}</span>,
                <span className="text-bold" style={{ fontSize: '0.9rem' }}>
                  {new Date(media?.criadoEm).toLocaleDateString(i18n.language)}
                </span>,
              ]}
              separator
            />
            <Tag
              icon={<i className={`${iconsEnum.time}`}></i>}
              labels={[
                <span style={{ fontSize: '0.9rem' }}>{t('hour')}</span>,
                <span className="text-bold" style={{ fontSize: '0.9rem' }}>
                  {formatHour(media?.obtidoEm)}
                </span>,
              ]}
              separator
            />
            {isContentApproved ? (
              <Tag
                icon={<i className={`${iconsEnum.gamification}`}></i>}
                labels={[
                  <span className="text-bold" style={{ fontSize: '0.9rem' }}>
                    {t('punctuated')}
                  </span>,
                ]}
                separator
              />
            ) : null}
          </div>
        </div>
        {media?.tipo === 'texto' && (
          <div className="post-content">
            <p className="post-text-content">{media?.legenda}</p>
          </div>
        )}
        {/* TODO: OUTROS TIPO DE CONTEÚDO */}
        {media?.tipo !== 'texto' && (
          <div className="post-content">
            <img src={media?.imagens?.resolucaoPadrao?.url} alt={media?.legenda} />
          </div>
        )}
        <MetricBarComponent media={media} />
      </div>
    )
  }, [media, formatHour, t, iconsEnum, i18n.language, isContentApproved])

  const postTiktok = useCallback(() => {
    return (
      <div className="wrapper-content-approval">
        <div className="infos-content-tag">
          <div className="infos-content">
            <Tag
              icon={<i className={`${iconsEnum.startDate}`}></i>}
              labels={[
                <span style={{ fontSize: '0.9rem' }}>{t('date')}</span>,
                <span className="text-bold" style={{ fontSize: '0.9rem' }}>
                  {new Date(media?.criadoEm).toLocaleDateString(i18n.language)}
                </span>,
              ]}
              separator
            />
            <Tag
              icon={<i className={`${iconsEnum.time}`}></i>}
              labels={[
                <span style={{ fontSize: '0.9rem' }}>{t('hour')}</span>,
                <span className="text-bold" style={{ fontSize: '0.9rem' }}>
                  {formatHour(media?.obtidoEm)}
                </span>,
              ]}
              separator
            />
            {isContentApproved ? (
              <Tag
                icon={<i className={`${iconsEnum.gamification}`}></i>}
                labels={[
                  <span className="text-bold" style={{ fontSize: '0.9rem' }}>
                    {t('punctuated')}
                  </span>,
                ]}
                separator
              />
            ) : null}
          </div>
        </div>
        <div className="post-content">
          <img src={media?.imagens?.resolucaoPadrao?.url} alt={media?.legenda} />
          <Button className="py-0 mt-1" rounded style={{ width: '100%' }} onClick={() => window.open(media?.link, '_blank')}>
            {t('see-video')}
            <i className="ml-2 fa-regular faU-up-right-from-square"></i>
          </Button>
        </div>
        <div className="post-description">
          <p className="description">
            <span className="username text-bold">@{media?.usuario?.username}</span>
            {` ${media?.legenda}`}
          </p>
        </div>
        <Tag
          labels={
            <div className="metadata-content">
              <span style={{ fontSize: '0.9rem' }}>
                <i className={`${iconsEnum.like}`}></i> {media?.upvotes || 0}
              </span>
              <span style={{ fontSize: '0.9rem' }}>
                <i className={`${iconsEnum.comment}`}></i> {media?.comentarios || 0}
              </span>
            </div>
          }
        />
      </div>
    )
  }, [media, formatHour, t, iconsEnum, i18n.language, isContentApproved])

  return (
    <div className={`content-approval-card ${className}`} style={style}>
      <div className="card-header">
        <div className="tag-user">
          <img
            src={`https://storage.googleapis.com/squid-profile-pictures/${userInCampaign?.idNetwork}.jpg`}
            alt={media?.usuario?.username}
            className="profile-image"
            onError={({ currentTarget }) => {
              currentTarget.onerror = null
              currentTarget.src = defaultImage
            }}
          />
          <span className="profile-name">{media?.usuario?.nome || media?.usuario?.username}</span>
        </div>
      </div>

      {socialNetwork === 'instagram' && postInstagram()}
      {socialNetwork === 'youtube' && postYoutube()}
      {socialNetwork === 'twitter' && postTwitter()}
      {socialNetwork === 'tiktok' && postTiktok()}
    </div>
  )
}
